import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UnsubscribeService } from '@app/infrastructure/unsubscribe/unsubscribe.service';
import {
	ICalculatePotentialSavingsParamsDTO,
	IPotentialSavingsDTO,
} from '@app/modules/potential-save-modal/dto/i-potential-savings.dto';
import { PotentialSavingsViews } from '@app/modules/potential-save-modal/potential-savings-views.enum';
import { PotentialSavingsService } from '@modules/potential-save-modal/potential-savings.service';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

@Component({
	selector: 'app-savings-breakdown',
	templateUrl: './savings-breakdown.component.html',
	styleUrls: ['./savings-breakdown.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [UnsubscribeService],
})
export class SavingsBreakdownComponent implements OnChanges {
	@Input()
	public set isAllocationsAndLinuxTabsAllowed(value: boolean) {
		this._isAllocationsAndLinuxTabsAllowed = window.location.href.includes('organization/general') ? value : true;
	}

	@Input() public potentialSaveData: IPotentialSavingsDTO;
	@Input() public isEDP: boolean;
	@Input() public isZestyEmployee: boolean;
	@Input() public isZestyDomain: boolean;
	@Input() public isMonthlyState: boolean;

	@Output() public openAwsCostExplorer: EventEmitter<void> = new EventEmitter<void>();
	@Output() public replaceModalView: EventEmitter<PotentialSavingsViews> = new EventEmitter<PotentialSavingsViews>();

	public isLoading = false;
	public errorMessage = '';
	public readonly form: FormGroup = this.initForm();
	public readonly potentialSaveModalView = PotentialSavingsViews;
	public readonly infoMessage = 'The maximum potential drop of \n' + 'workloads to protect against.';
	private _isAllocationsAndLinuxTabsAllowed: boolean = false;
	private readonly defaultErrorMessage = 'Something went wrong, please try again later';

	public get isAllocationsAndLinuxTabsAllowed(): boolean {
		return this._isAllocationsAndLinuxTabsAllowed;
	}

	public get coveringSavingsValue(): number {
		return this.isMonthlyState
			? this.potentialSaveData.monthlySupportedOndemandSavings
			: this.potentialSaveData.annualOndemandAndShiftingSavings;
	}

	public get coveringSavingsText(): string {
		return this.isMonthlyState
			? 'Savings available by covering On-demand compute with RIs'
			: 'Annual savings available';
	}

	public get reallocatingSavingsValue(): number {
		return this.isMonthlyState
			? this.potentialSaveData.monthlyShiftingAdditionalSavings
			: this.potentialSaveData.annualExpiredCommitmentsRenewalSavings;
	}

	public get reallocatingSavingsText(): string {
		return this.isMonthlyState
			? 'Savings available by expanding and reallocating existing saving plans'
			: 'Renewing expired commitments';
	}

	public get savings(): number {
		return this.isMonthlyState ? this.potentialSaveData.monthlyPS : this.potentialSaveData.annualPS;
	}

	constructor(
		private unsubscribe$: UnsubscribeService,
		private potentialSavingsService: PotentialSavingsService,
		private cdr: ChangeDetectorRef,
	) {}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.potentialSaveData?.currentValue) {
			const workloadDropToleranceValue = !!this.potentialSaveData.usedFlexibility
				? this.potentialSaveData.usedFlexibility * 100
				: this.potentialSaveData.defaultFlexibility * 100;
			const sriCoverage = this.isEDP ? this.potentialSaveData.coverage.optimalPlan.SRI * 100 : 0;
			const criCoverage = this.isEDP ? this.potentialSaveData.coverage.optimalPlan.CRI * 100 : 0;
			const spCoverage = this.isEDP ? this.potentialSaveData.coverage.optimalPlan.SP * 100 : 0;
			const newValue = {
				flexibility: Math.round(workloadDropToleranceValue),
				sri_coverage: Math.round(sriCoverage),
				cri_coverage: Math.round(criCoverage),
				sp_coverage: Math.round(spCoverage),
			};
			this.form.patchValue(newValue, { emitEvent: false });
			this.verifyLastUpdatedDate(this.potentialSaveData.updatedAt);
		}
	}

	public getFormControl(path: string): FormControl {
		return this.form.get(path) as FormControl;
	}

	public changeModalView(view: PotentialSavingsViews): void {
		this.replaceModalView.emit(view);
	}

	public openAwsCostExplorerLink(): void {
		this.openAwsCostExplorer.emit();
	}

	public submitForm(): void {
		if (this.form.invalid) {
			return;
		}

		this.isLoading = true;
		this.errorMessage = '';
		this.form.setErrors(null);
		this.cdr.detectChanges();
		this.handlePotentialSavingsParams()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.isLoading = false;
				this.cdr.detectChanges();
			});
	}

	private initForm(): FormGroup {
		return new FormGroup({
			flexibility: new FormControl(null, [Validators.min(0), Validators.max(100)]),
			sri_coverage: new FormControl(null, [Validators.min(0), Validators.max(100)]),
			cri_coverage: new FormControl(null, [Validators.min(0), Validators.max(100)]),
			sp_coverage: new FormControl(null, [Validators.min(0), Validators.max(100)]),
		});
	}

	private handlePotentialSavingsParams(): Observable<Partial<IPotentialSavingsDTO>> {
		const params: ICalculatePotentialSavingsParamsDTO = {
			flexibility: this.form.value.flexibility / 100,
			sri_coverage: this.form.value.sri_coverage / 100,
			cri_coverage: this.form.value.cri_coverage / 100,
			sp_coverage: this.form.value.sp_coverage / 100,
		};

		return this.potentialSavingsService.getPotentialSavingsByParams(params).pipe(
			tap((res) => {
				if (!res) {
					this.errorMessage = this.defaultErrorMessage;
				}
			}),
			catchError((error) => {
				if (typeof error.error?.detail === 'string') {
					this.errorMessage = error.error?.detail;
				} else if (Array.isArray(error.error?.detail)) {
					this.errorMessage = error.error?.detail[0]?.msg;
				} else {
					this.errorMessage = this.defaultErrorMessage;
				}

				return of(null);
			}),
		);
	}

	private verifyLastUpdatedDate(givenDate: string): void {
		const currentDate = moment().format('YYYY-MM-DD');
		if (givenDate !== currentDate) {
			this.form.disable();
		}
	}
}
